/**
 * フィーチャーフラグ管理システム
 *
 * アプリケーション全体の機能の有効/無効を制御
 *
 * ## 環境別の動作
 * - **開発環境**: ローカルストレージに設定を永続化
 * - **本番環境**: 環境変数とURLパラメータのみ使用（ローカルストレージ不使用）
 */

import { StorageError } from '@src/errors';
import { errorLogger } from '@src/services/errorLogger';
import { resolveBooleanFlag } from '@src/utils/env';

export enum FeatureFlag {
  // ドキュメントタイプ
  USE_RIREKISHO = 'use_rirekisho',
  USE_RESUME = 'use_resume',

  // 認証・ユーザー管理機能
  AUTH_ENABLED = 'auth_enabled',

  // UI機能
  FOCUS_MODE = 'focus_mode',

  // AI機能
  AI_REWRITE = 'ai_rewrite',
  TYPO_FIX = 'typo_fix',
}

/**
 * フィーチャーフラグの設定
 */
interface FeatureFlagConfig {
  [FeatureFlag.USE_RIREKISHO]: boolean;
  [FeatureFlag.USE_RESUME]: boolean;
  [FeatureFlag.AUTH_ENABLED]: boolean;
  [FeatureFlag.FOCUS_MODE]: boolean;
  [FeatureFlag.AI_REWRITE]: boolean;
  [FeatureFlag.TYPO_FIX]: boolean;
}

/**
 * 環境変数からフィーチャーフラグを読み込み
 */
const getEnvironmentFlags = (): Partial<FeatureFlagConfig> => {
  const flags: Partial<FeatureFlagConfig> = {};

  if (typeof window !== 'undefined') {
    // ブラウザ環境
    const urlParams = new URLSearchParams(window.location.search);

    for (const flag of Object.values(FeatureFlag)) {
      const value = urlParams.get(flag);
      if (value !== null) {
        flags[flag as keyof FeatureFlagConfig] = value === 'true';
      }
    }
  }

  // 環境変数から読み込み
  const envFlagOverrides: [FeatureFlag, string | undefined, boolean][] = [
    [FeatureFlag.USE_RIREKISHO, import.meta.env.VITE_USE_RIREKISHO, false],
    [FeatureFlag.USE_RESUME, import.meta.env.VITE_USE_RESUME, true],
    [FeatureFlag.AUTH_ENABLED, import.meta.env.VITE_AUTH_ENABLED, false],
    [FeatureFlag.FOCUS_MODE, import.meta.env.VITE_FOCUS_MODE, false],
    [FeatureFlag.AI_REWRITE, import.meta.env.VITE_AI_REWRITE, false],
    [FeatureFlag.TYPO_FIX, import.meta.env.VITE_TYPO_FIX, false],
  ];

  for (const [flag, rawValue, defaultValue] of envFlagOverrides) {
    if (rawValue !== undefined) {
      flags[flag] = resolveBooleanFlag(rawValue, defaultValue);
    }
  }

  return flags;
};

/**
 * ローカルストレージからフィーチャーフラグを読み込み
 * 開発環境時のみローカルストレージを使用
 */
const getLocalStorageFlags = (): Partial<FeatureFlagConfig> => {
  // 本番環境ではローカルストレージを使用しない
  if (!import.meta.env.DEV) {
    return {};
  }

  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = localStorage.getItem('featureFlags');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    const storageError = new StorageError(
      'Failed to load feature flags from localStorage',
      undefined,
      { cause: error }
    );
    errorLogger.logError(storageError, {
      context: 'フィーチャーフラグ読み込み',
      action: 'loadFromStorage',
    });
  }

  return {};
};

/**
 * デフォルトのフィーチャーフラグ設定
 */
const defaultFlags: FeatureFlagConfig = {
  [FeatureFlag.USE_RIREKISHO]: false,
  [FeatureFlag.USE_RESUME]: true,
  [FeatureFlag.AUTH_ENABLED]: false,
  [FeatureFlag.FOCUS_MODE]: false,
  [FeatureFlag.AI_REWRITE]: false,
  [FeatureFlag.TYPO_FIX]: false,
};

/**
 * フィーチャーフラグマネージャー
 *
 * 優先順位（本番環境）:
 * 1. URLパラメータ
 * 2. 環境変数
 * 3. デフォルト値
 *
 * 優先順位（開発環境）:
 * 1. URLパラメータ
 * 2. ローカルストレージ（開発者パネルでの変更を反映）
 * 3. 環境変数
 * 4. デフォルト値
 *
 * 本番環境ではローカルストレージは使用されません。
 */
class FeatureFlagManager {
  private flags: FeatureFlagConfig;
  private listeners: Set<(flags: FeatureFlagConfig) => void> = new Set();

  constructor() {
    // 優先順位:
    // - 本番環境: URL > 環境変数 > デフォルト
    // - 開発環境: URL > ローカルストレージ > 環境変数 > デフォルト
    //   （開発環境ではローカルストレージを環境変数より優先してdevパネルでの変更を反映）
    const envFlags = getEnvironmentFlags();
    const localFlags = getLocalStorageFlags();

    this.flags = {
      ...defaultFlags,
      ...envFlags,
      ...(import.meta.env.DEV ? localFlags : {}),
    };
  }

  /**
   * フィーチャーフラグの取得
   */
  isEnabled(flag: FeatureFlag): boolean {
    return this.flags[flag] ?? false;
  }

  /**
   * フィーチャーフラグの設定
   */
  setFlag(flag: FeatureFlag, enabled: boolean): void {
    this.flags[flag] = enabled;
    this.persistFlags();
    this.notifyListeners();
  }

  /**
   * 複数のフィーチャーフラグを一括設定
   */
  setFlags(flags: Partial<FeatureFlagConfig>): void {
    Object.assign(this.flags, flags);
    this.persistFlags();
    this.notifyListeners();
  }

  /**
   * すべてのフィーチャーフラグを取得
   */
  getAllFlags(): FeatureFlagConfig {
    return { ...this.flags };
  }

  /**
   * フィーチャーフラグのリセット
   */
  resetFlags(): void {
    this.flags = { ...defaultFlags };
    this.persistFlags();
    this.notifyListeners();
  }

  /**
   * リスナーの登録
   */
  subscribe(listener: (flags: FeatureFlagConfig) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * ローカルストレージへの永続化
   * 開発環境時のみローカルストレージに保存
   */
  private persistFlags(): void {
    // 本番環境ではローカルストレージに保存しない
    if (!import.meta.env.DEV) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem('featureFlags', JSON.stringify(this.flags));
    } catch (error) {
      const storageError = new StorageError(
        'Failed to persist feature flags',
        { flags: this.flags },
        { cause: error }
      );
      errorLogger.logError(storageError, {
        context: 'フィーチャーフラグ保存',
        action: 'persistFlags',
      });
    }
  }

  /**
   * リスナーへの通知
   */
  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.flags);
    }
  }
}

// シングルトンインスタンス
export const featureFlagManager = new FeatureFlagManager();

/**
 * React Hook: フィーチャーフラグの使用
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const [enabled, setEnabled] = React.useState(() => featureFlagManager.isEnabled(flag));

  React.useEffect(() => {
    const unsubscribe = featureFlagManager.subscribe((flags) => {
      setEnabled(flags[flag]);
    });

    return unsubscribe;
  }, [flag]);

  return enabled;
}

// React import for hooks
import React from 'react';

/**
 * 特定のフィーチャーフラグが有効かどうかを判定
 * @deprecated - featureFlagManager.isEnabled() を使用してください
 */
export function isFeatureEnabled(feature: keyof typeof FeatureFlag | string): boolean {
  // 旧システムとの互換性のため、文字列を受け取る
  const flag =
    typeof feature === 'string' && feature.toUpperCase() in FeatureFlag
      ? (feature.toLowerCase() as FeatureFlag)
      : (feature as FeatureFlag);
  return featureFlagManager.isEnabled(flag);
}
